import React, { useState, useEffect, useCallback } from 'react';
import Button from '../BaseComponents/Button';
import ResourceCenterTable from './ResourceCenterTable';
import ResourceFormModal from './ResourceFormModal';
import ResourceViewModal from './ResourceViewModal';
import { listResources, deleteResource, getResourceById, Resource, ListResourcesRequest } from '../../api/resourceCenter';
import './ResourceCenter.scss';

const ResourceCenter: React.FC = () => {
  const [resourcesList, setResourcesList] = useState<Resource[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortingLoading, setSortingLoading] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [viewingResource, setViewingResource] = useState<Resource | null>(null);
  const [searchQuery] = useState('');
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const fetchResources = useCallback(async () => {
    try {
      setIsLoading(true);
      const params: ListResourcesRequest = {
        limit: 50,
        offset: 0,
        search: searchQuery || undefined
      };
      const response = await listResources(params);
      setResourcesList(response.resources);
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources, sortField, sortOrder]);

  const onAddResourceBtnClick = () => {
    setEditingResource(null);
    setIsFormModalOpen(true);
  };

  const onViewResource = async (resource: Resource) => {
    try {
      // Fetch full resource details
      const fullResource = await getResourceById(resource.id);
      setViewingResource(fullResource);
      setIsViewModalOpen(true);
    } catch (error) {
      console.error('Error fetching resource details:', error);
      // Fallback to using the resource from the list
      setViewingResource(resource);
      setIsViewModalOpen(true);
    }
  };

  const onEditResource = (resource: Resource) => {
    setEditingResource(resource);
    setIsFormModalOpen(true);
  };

  const onEditFromView = (resource: Resource) => {
    setEditingResource(resource);
    setIsViewModalOpen(false);
    setIsFormModalOpen(true);
  };

  const onDeleteResource = async (resourceId: number) => {
    try {
      await deleteResource({ resource_id: resourceId });
      await fetchResources();
      setIsViewModalOpen(false);
      setViewingResource(null);
    } catch (error) {
      console.error('Error deleting resource:', error);
    }
  };

  const onCloseFormModal = () => {
    setIsFormModalOpen(false);
    setEditingResource(null);
  };

  const onCloseViewModal = () => {
    setIsViewModalOpen(false);
    setViewingResource(null);
  };

  const onResourceSaved = async () => {
    await fetchResources();
    setIsFormModalOpen(false);
    setEditingResource(null);
  };

  const handleSort = async (field: string) => {
    let newOrder: 'asc' | 'desc' = 'asc';
    if (sortField === field && sortOrder === 'asc') {
      newOrder = 'desc';
    }
    
    setSortField(field);
    setSortOrder(newOrder);
    
    setSortingLoading(true);
    await new Promise(resolve => setTimeout(resolve, 300));
    setSortingLoading(false);
  };

  return (
    <>
      <div className='search-row-container'>
        <div className='search-and-filter-container'>
          <div className='count-container'>Content ({resourcesList.length})</div>
        </div>
        <Button
          isSecondary={true}
          text='Add Content'
          onClick={onAddResourceBtnClick}
        />
      </div>

      <div className='resource-center-container'>
        <ResourceCenterTable 
          resourcesList={resourcesList} 
          isLoading={isLoading}
          sortingLoading={sortingLoading}
          onViewResource={onViewResource}
          onEditResource={onEditResource}
          onDeleteResource={onDeleteResource}
          onSort={handleSort}
        />
      </div>

      {isFormModalOpen && (
        <ResourceFormModal
          isOpen={isFormModalOpen}
          onClose={onCloseFormModal}
          resourceToEdit={editingResource}
          onResourceSaved={onResourceSaved}
          onDeleteResource={onDeleteResource}
        />
      )}

      {isViewModalOpen && (
        <ResourceViewModal
          isOpen={isViewModalOpen}
          onClose={onCloseViewModal}
          resource={viewingResource}
          onEdit={onEditFromView}
          onDelete={onDeleteResource}
        />
      )}
    </>
  );
};

export default ResourceCenter;


